import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useInfiniteQuery } from "@tanstack/react-query";
import { AppDispatch, RootState } from "app/store";
import { RestrictedFeatureSource } from "app/types";
import I18n from "common/i18n";
import { usePlaySound } from "common/utils";
import { ChatInputBar } from "common/elements";
import { AppUser } from "features/user/types";
import { PlanType } from "features/subscription/types";
import { updateAiChatUsage } from "features/subscription";
import { Message, MessageRole } from "features/assistant/types";
import { MessageArea } from "features/assistant/components/MessageArea";
import { getAIChatHistory, sendMessageSSE } from "features/assistant/api";
import {
  usePlan,
  usePlanLimits,
  useEnforcePlanLimit,
  useOpenUpgradeOrPrompt,
} from "features/subscription/hooks";

interface AIChatProps {
  user: AppUser;
  selected: boolean;
}

export const AIChat = ({ user, selected }: AIChatProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const play = usePlaySound();
  const plan = usePlan();
  const limits = usePlanLimits();
  const aiChatEnabled = useSelector((state: RootState) => state.config.userConfig.ai_chat);
  const enforcePlanLimit = useEnforcePlanLimit();
  const openUpgradeOrPrompt = useOpenUpgradeOrPrompt();
  const abortControllerRef = useRef<AbortController | null>(null);
  const greetingShown = useRef(false);

  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [currentMessages, setCurrentMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limitError, setLimitError] = useState<string | null>(null);

  // 使用状況（Redux storeから取得）
  const usageInfo = {
    used: limits.aiChatDaily.used,
    remaining: limits.aiChatDaily.remaining,
    limit: limits.aiChatDaily.limit,
    isUnlimited: limits.aiChatDaily.unlimited,
  };

  // 履歴取得用の型
  type HistoryData = Awaited<ReturnType<typeof getAIChatHistory>>;

  // 履歴取得（ページング対応）
  const {
    data: historyData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingHistory,
  } = useInfiniteQuery<HistoryData, Error, { pages: HistoryData[]; pageParams: unknown[] }>({
    queryKey: ["ai-chat-history", user.uid, currentSessionId],
    queryFn: ({ pageParam }) =>
      getAIChatHistory({
        uid: user.uid,
        beforeSessionId: pageParam as string | undefined,
      }),
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    initialPageParam: undefined,
    enabled: selected,
    staleTime: 1000 * 60 * 5, // 5分
  });

  // 過去のセッション一覧（フラット化）
  const historySessions = historyData?.pages.flatMap((page) => page.sessions) || [];

  // 履歴取得後、最新セッションのIDをcurrentSessionIdにセット
  useEffect(() => {
    if (!historyData || currentSessionId) return;
    const allSessions = historyData.pages.flatMap((p) => p.sessions);
    if (allSessions.length === 0) return;
    // sessionsは時系列順（古い→新しい）、最後が最新
    const latestSession = allSessions[allSessions.length - 1];
    setCurrentSessionId(latestSession.id);
    greetingShown.current = true;
  }, [historyData, currentSessionId]);

  // 選択時に制限チェック、選択解除時にリセット
  useEffect(() => {
    if (!selected) return;

    // 制限に達している場合、エラーメッセージを表示
    if (
      (plan === PlanType.TRIAL || plan === PlanType.FREE) &&
      !usageInfo.isUnlimited &&
      usageInfo.remaining <= 0
    ) {
      setLimitError(I18n.t("MSG_ASSISTANT_LIMIT_REACHED"));
    }

    return () => {
      setLimitError(null);
    };
  }, [selected, plan, usageInfo.isUnlimited, usageInfo.remaining]);

  // 選択時にローカルで挨拶メッセージを表示（DBには保存しない）
  useEffect(() => {
    if (!selected || greetingShown.current || isLoadingHistory) return;
    if (currentMessages.length > 0 || historySessions.length > 0) return;

    greetingShown.current = true;

    // 保護者許可チェック
    if (!aiChatEnabled) {
      setLimitError(I18n.t("MSG_ASSISTANT_PARENT_NOT_ALLOWED"));
      return;
    }

    const greeting: Message = {
      id: `greeting-${Date.now()}`,
      sessionId: "",
      role: MessageRole.ASSISTANT,
      content: I18n.t("MSG_ASSISTANT_GREETING", { name: user.display_name || "" }),
      createdAt: dayjs().toISOString(),
    };
    setCurrentMessages([greeting]);
  }, [
    selected,
    currentMessages.length,
    aiChatEnabled,
    plan,
    user.display_name,
    isLoadingHistory,
    historySessions.length,
  ]);

  // 上スクロールでページング
  const handleScrollTop = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // メッセージ送信
  const handleSendMessage = useCallback(
    async (text: string, imageUrl?: string) => {
      if (!user?.uid || (!text.trim() && !imageUrl) || isTyping) return;
      // TRIAL は新 Dialog、FREE は RestrictedFeatureDialog
      if (
        (plan === PlanType.TRIAL || plan === PlanType.FREE) &&
        !usageInfo.isUnlimited &&
        usageInfo.used >= usageInfo.limit
      ) {
        enforcePlanLimit(RestrictedFeatureSource.AI_CHAT_LIMIT);
        return;
      }

      const content = text.trim();
      const imageUrls = imageUrl ? [imageUrl] : undefined;
      play();

      const userMessage: Message = {
        id: `msg-${Date.now()}`,
        sessionId: currentSessionId || "",
        role: MessageRole.USER,
        content,
        images: imageUrls?.map((url) => ({ url })),
        createdAt: dayjs().toISOString(),
      };
      setCurrentMessages((prev) => [...prev, userMessage]);
      setIsTyping(true);
      setError(null);

      abortControllerRef.current = new AbortController();

      try {
        await sendMessageSSE({
          uid: user.uid,
          sessionId: currentSessionId || "new",
          message: content,
          imageUrls,
          pageUrl: window.location.href,
          signal: abortControllerRef.current.signal,
          callbacks: {
            onMessageStart: (messageId, sessionId) => {
              // 新規セッションの場合、セッションIDを保存
              if (!currentSessionId) {
                setCurrentSessionId(sessionId);
              }
              const aiMessage: Message = {
                id: messageId,
                sessionId,
                role: MessageRole.ASSISTANT,
                content: "",
                createdAt: dayjs().toISOString(),
              };
              setCurrentMessages((prev) => [...prev, aiMessage]);
            },
            onContentDelta: (delta) => {
              setCurrentMessages((prev) => {
                const updated = [...prev];
                const lastMsg = updated[updated.length - 1];
                if (lastMsg && lastMsg.role === MessageRole.ASSISTANT) {
                  updated[updated.length - 1] = { ...lastMsg, content: lastMsg.content + delta };
                }
                return updated;
              });
            },
            onMessageEnd: (_messageId, usage) => {
              setIsTyping(false);
              abortControllerRef.current = null;
              if (usage) {
                dispatch(
                  updateAiChatUsage({
                    uid: user.uid,
                    used: usage.used,
                    remaining: usage.remaining,
                  }),
                );
              }
            },
            onError: (code, message) => {
              setIsTyping(false);
              setError(message);
              setCurrentMessages((prev) => {
                const lastMsg = prev[prev.length - 1];
                if (lastMsg && lastMsg.role === MessageRole.ASSISTANT && !lastMsg.content) {
                  return prev.slice(0, -1);
                }
                return prev;
              });
              console.error(`AI Chat Error [${code}]:`, message);
              abortControllerRef.current = null;
              if (code === "RATE_LIMIT_EXCEEDED" || code === "PLAN_NOT_ALLOWED") {
                setLimitError(message);
              }
            },
            onSessionReset: (newSessionId) => {
              setCurrentMessages((prev) => {
                const updated = [...prev];
                let count = 0;
                for (let i = updated.length - 1; i >= 0 && count < 2; i--) {
                  if (updated[i].sessionId === currentSessionId) {
                    updated[i] = { ...updated[i], sessionId: newSessionId };
                    count++;
                  } else {
                    break;
                  }
                }
                return updated;
              });
              setCurrentSessionId(newSessionId);
            },
          },
        });
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          setIsTyping(false);
        } else {
          setIsTyping(false);
          setError(I18n.t("MSG_CHAT_SEND_FAILED_MESSAGE"));
          console.error("Failed to send message:", err);
        }
        abortControllerRef.current = null;
      }
    },
    [
      dispatch,
      user?.uid,
      currentSessionId,
      isTyping,
      play,
      plan,
      enforcePlanLimit,
      usageInfo.used,
      usageInfo.remaining,
      usageInfo.limit,
      usageInfo.isUnlimited,
      aiChatEnabled,
    ],
  );

  // 停止
  const handleStop = useCallback(() => {
    play();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // 空のAIメッセージが残っていたら削除
    setCurrentMessages((prev) => {
      const lastMsg = prev[prev.length - 1];
      if (lastMsg && lastMsg.role === MessageRole.ASSISTANT && !lastMsg.content) {
        return prev.slice(0, -1);
      }
      return prev;
    });
    setIsTyping(false);
  }, [play]);

  if (!selected) return null;

  return (
    <div className="flex flex-col w-full h-full bg-white rounded-2xl border-solid border-orange-alt border-[1px] overflow-hidden">
      {/* 制限エラー表示 */}
      {limitError && (
        <div className="flex-row-el justify-center items-center gap-4 py-3 bg-orange-100">
          <p className="text text-sm text-white">{limitError}</p>
          <button
            className="flex-row-el items-center gap-1 px-3 py-1 bg-safe text-white rounded-full text-sm cursor-pointer active:opacity-70 transition-colors border-0"
            onClick={() => {
              play();
              openUpgradeOrPrompt();
            }}
          >
            <span className="material-symbols-outlined !text-base">sync_alt</span>
            {I18n.t("MSG_PLAN_CHANGE_BTN")}
          </button>
        </div>
      )}

      {/* メッセージエリア */}
      <MessageArea
        messages={currentMessages}
        isTyping={isTyping}
        currentSessionId={currentSessionId}
        userIcon={user.icon}
        historySessions={historySessions}
        isLoadingHistory={isLoadingHistory}
        isFetchingNextPage={isFetchingNextPage}
        onScrollTop={handleScrollTop}
      />

      {/* エラー表示 */}
      {error && (
        <div className="flex-row-center py-2">
          <p className="text text-red-80">{error}</p>
        </div>
      )}

      {/* 入力エリア */}
      <ChatInputBar
        onSend={handleSendMessage}
        onStop={handleStop}
        isTyping={isTyping}
        combineImageAndText
        disabled={!aiChatEnabled}
        placeholder={I18n.t("MSG_USER_PROFILE_MESSAGE_CHAT_PLACEHOLDER")}
      />
    </div>
  );
};
